import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { listingsAPI, messagesAPI, savedListingsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatPrice, formatNumber, formatDate, getConditionBadgeColor, getConditionLabel, getCategoryLabel } from '../lib/utils';
import { ListingGrid } from '../components/listings/ListingGrid';
import { toast } from 'sonner';
import {
  Heart,
  Share2,
  MapPin,
  Calendar,
  Gauge,
  Clock,
  Phone,
  Mail,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Star,
  Shield,
  User,
  Building,
  FileText,
  ArrowLeft,
  ExternalLink,
} from 'lucide-react';

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [similarListings, setSimilarListings] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [contactMessage, setContactMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await listingsAPI.getById(id);
        setListing(response.data);

        // Track view (skip if the seller is viewing their own listing)
        if (!user || user.id !== response.data.seller_id) {
          listingsAPI.incrementView(id).catch(() => {});
        }

        // Fetch similar listings
        const similarRes = await listingsAPI.getAll({
          category: response.data.category,
          limit: 4,
        });
        setSimilarListings(similarRes.data.filter(l => l.id !== id).slice(0, 4));
        
        // Check if saved
        if (isAuthenticated) {
          const savedRes = await savedListingsAPI.getAll();
          setIsSaved(savedRes.data.some(l => l.id === id));
        }
      } catch (error) {
        console.error('Error fetching listing:', error);
        toast.error('No se pudo cargar el anuncio');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isAuthenticated, user]);

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para guardar anuncios');
      return;
    }
    
    try {
      if (isSaved) {
        await savedListingsAPI.unsave(id);
        setIsSaved(false);
        toast.success('Anuncio eliminado de guardados');
      } else {
        await savedListingsAPI.save(id);
        setIsSaved(true);
        toast.success('Anuncio guardado');
      }
    } catch (error) {
      toast.error('No se pudo actualizar el anuncio guardado');
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Enlace copiado al portapapeles');
  };

  const handleWhatsAppShare = () => {
    const title = `${listing.year} ${listing.make} ${listing.model}`;
    // Use the backend share endpoint so WhatsApp fetches OG meta tags and
    // renders a rich preview card. Humans get redirected to /listings/:id.
    const shareUrl = `${window.location.origin}/api/share/listing/${listing.id}`;
    const text = `\u00A1Mira este ${title} en VehiMax! ${formatPrice(listing.price)} - ${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  const handleContactSeller = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para contactar al vendedor');
      return;
    }
    
    if (!contactMessage.trim()) {
      toast.error('Escribe un mensaje');
      return;
    }
    
    setSendingMessage(true);
    try {
      await messagesAPI.send({
        listing_id: id,
        receiver_id: listing.seller_id,
        message: contactMessage,
      });
      toast.success('Mensaje enviado al vendedor');
      setContactMessage('');
      setContactDialogOpen(false);
    } catch (error) {
      toast.error('No se pudo enviar el mensaje');
    } finally {
      setSendingMessage(false);
    }
  };

  const nextImage = () => {
    if (listing?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % listing.images.length);
    }
  };

  const prevImage = () => {
    if (listing?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + listing.images.length) % listing.images.length);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-4">
            <Skeleton className="aspect-[16/10] w-full rounded-md" />
            <div className="flex gap-2">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="w-24 h-20 rounded-md" />
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 text-center">
        <h1 className="font-heading text-3xl font-bold uppercase mb-4">Anuncio no encontrado</h1>
        <p className="text-muted-foreground mb-8">Este anuncio puede haber sido eliminado o ya no está disponible.</p>
        <Link to="/browse">
          <Button className="btn-industrial">Explorar anuncios</Button>
        </Link>
      </div>
    );
  }

  const images = listing.images?.length > 0 
    ? listing.images 
    : [{ image_url: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&h=600&fit=crop' }];

  return (
    <div className="min-h-screen bg-secondary" data-testid="listing-detail-page">
      {/* Back Button */}
      <div className="bg-white border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-muted-foreground hover:text-foreground"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />Volver a los anuncios</Button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Images & Details */}
          <div className="lg:col-span-8 space-y-6">
            {/* Image Gallery */}
            <div className="bg-white border border-border rounded-md overflow-hidden">
              <div className="relative aspect-[16/10]">
                <img
                  src={images[currentImageIndex]?.image_url}
                  alt={`${listing.year} ${listing.make} ${listing.model}`}
                  className="w-full h-full object-cover"
                  data-testid="main-image"
                />
                
                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md"
                      onClick={prevImage}
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md"
                      onClick={nextImage}
                      data-testid="next-image-btn"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </>
                )}

                {/* Badges */}
                <div className="absolute top-4 left-4 flex gap-2">
                  {listing.featured && (
                    <Badge className="bg-accent text-white">
                      <Star className="h-3 w-3 mr-1 fill-current" />Destacado</Badge>
                  )}
                  <Badge className={`${getConditionBadgeColor(listing.condition)} text-white`}>
                    {getConditionLabel(listing.condition)}
                  </Badge>
                </div>

                {/* Image Counter */}
                <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 text-sm rounded-sm">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex gap-2 p-4 overflow-x-auto">
                  {images.map((img, index) => (
                    <button
                      key={img.image_url ? `${img.image_url}-${index}` : index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-16 rounded-sm overflow-hidden border-2 transition-colors ${
                        index === currentImageIndex ? 'border-accent' : 'border-transparent'
                      }`}
                    >
                      <img
                        src={img.image_url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Specifications */}
            <div className="bg-white border border-border rounded-md p-6">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-6">Especificaciones</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div>
                  <span className="text-sm text-muted-foreground uppercase tracking-wide">Año</span>
                  <p className="font-semibold text-lg">{listing.year}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground uppercase tracking-wide">Marca</span>
                  <p className="font-semibold text-lg">{listing.make}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground uppercase tracking-wide">Modelo</span>
                  <p className="font-semibold text-lg">{listing.model}</p>
                </div>
                {listing.mileage && (
                  <div>
                    <span className="text-sm text-muted-foreground uppercase tracking-wide">Kilometraje</span>
                    <p className="font-semibold text-lg">{formatNumber(listing.mileage)} km</p>
                  </div>
                )}
                {listing.engine_hours && (
                  <div>
                    <span className="text-sm text-muted-foreground uppercase tracking-wide">Horas de motor</span>
                    <p className="font-semibold text-lg">{formatNumber(listing.engine_hours)} hrs</p>
                  </div>
                )}
                <div>
                  <span className="text-sm text-muted-foreground uppercase tracking-wide">Condición</span>
                  <p className="font-semibold text-lg">{getConditionLabel(listing.condition)}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground uppercase tracking-wide">Categoría</span>
                  <p className="font-semibold text-lg capitalize">{getCategoryLabel(listing.category)}</p>
                </div>
                {listing.vin && (
                  <div className="col-span-2">
                    <span className="text-sm text-muted-foreground uppercase tracking-wide">VIN</span>
                    <p className="font-semibold text-lg font-mono">{listing.vin}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {listing.description && (
              <div className="bg-white border border-border rounded-md p-6">
                <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-4">Descripción</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {listing.description}
                </p>
              </div>
            )}
          </div>

          {/* Right Column - Price & Contact */}
          <div className="lg:col-span-4 space-y-6">
            {/* Price Card */}
            <div className="bg-white border border-border rounded-md p-6 sticky top-24">
              <div className="mb-4">
                <span className="text-sm text-muted-foreground uppercase tracking-wide">
                  {getCategoryLabel(listing.category)}
                </span>
                <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mt-1" data-testid="listing-title">
                  {listing.year} {listing.make} {listing.model}
                </h1>
              </div>

              <div className="price-tag text-4xl mb-6" data-testid="listing-price">
                {formatPrice(listing.price)}
              </div>

              {/* Location */}
              <div className="flex items-center gap-2 text-muted-foreground mb-6">
                <MapPin className="h-4 w-4" />
                <span>{listing.location_city}, {listing.location_state}</span>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-accent w-full" data-testid="contact-seller-btn">
                      <MessageSquare className="h-4 w-4 mr-2" />Contactar al vendedor</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Contactar al vendedor</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="text-sm text-muted-foreground">
                        Envía un mensaje sobre: <strong>{listing.year} {listing.make} {listing.model}</strong>
                      </div>
                      <Textarea
                        placeholder="Hola, me interesa este anuncio..."
                        value={contactMessage}
                        onChange={(e) => setContactMessage(e.target.value)}
                        rows={4}
                        data-testid="contact-message-input"
                      />
                      <Button
                        className="btn-accent w-full"
                        onClick={handleContactSeller}
                        disabled={sendingMessage}
                        data-testid="send-message-btn"
                      >
                        {sendingMessage ? 'Enviando...' : 'Enviar mensaje'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className={`flex-1 ${isSaved ? 'text-red-500 border-red-500' : ''}`}
                    onClick={handleSave}
                    data-testid="save-listing-btn"
                  >
                    <Heart className={`h-4 w-4 mr-2 ${isSaved ? 'fill-current' : ''}`} />
                    {isSaved ? 'Guardado' : 'Guardar'}
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={handleShare} data-testid="share-btn">
                    <Share2 className="h-4 w-4 mr-2" />Compartir</Button>
                </div>

                <Button
                  className="w-full mt-3 bg-[#25D366] hover:bg-[#1EBE5D] text-white"
                  onClick={handleWhatsAppShare}
                  data-testid="whatsapp-share-btn"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 mr-2" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Compartir en WhatsApp
                </Button>
              </div>

              <Separator className="my-6" />

              {/* Seller Info */}
              <div>
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight mb-4">Información del vendedor</h3>
                <Link
                  to={`/sellers/${listing.seller_id}`}
                  className="block space-y-3 group"
                  data-testid="seller-profile-link"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-secondary p-2 rounded-sm group-hover:bg-accent group-hover:text-white transition-colors">
                      {listing.seller_role === 'dealer' ? (
                        <Building className="h-5 w-5" />
                      ) : (
                        <User className="h-5 w-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold group-hover:text-accent transition-colors">
                        {listing.seller_company || listing.seller_name || 'Vendedor particular'}
                      </p>
                      <p className="text-sm text-muted-foreground capitalize">
                        {listing.seller_role === 'dealer' ? 'Concesionario verificado' : 'Vendedor particular'}
                      </p>
                    </div>
                  </div>

                  {listing.seller_role === 'dealer' && (
                    <div className="flex items-center gap-2 text-sm text-accent">
                      <Shield className="h-4 w-4" />Vendedor verificado</div>
                  )}

                  <div className="text-xs text-accent underline-offset-2 group-hover:underline">Ver perfil completo e inventario →</div>
                </Link>
              </div>

              <Separator className="my-6" />

              {/* Listing Info */}
              <div className="text-sm text-muted-foreground space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Publicado el {formatDate(listing.created_at)}
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  ID: {listing.id.slice(0, 8)}...
                </div>
              </div>
            </div>

            {/* Financing CTA */}
            <div className="bg-primary text-white p-6 rounded-md">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight mb-2">¿Necesitas financiamiento?</h3>
              <p className="text-white/70 text-sm mb-4">Obtén pre-aprobación de financiamiento para vehículos comerciales en minutos.</p>
              <Button variant="outline" className="w-full border-white text-white hover:bg-white hover:text-primary">Obtener pre-aprobación<ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* Similar Listings */}
        {similarListings.length > 0 && (
          <div className="mt-16">
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-8">Anuncios similares</h2>
            <ListingGrid listings={similarListings} columns={4} />
          </div>
        )}
      </div>
    </div>
  );
}
